import React, { useMemo, useState } from 'react'
import { MotifId } from '../types'
import { MOTIFS } from '../data/motifs'
import { COLOR_VALUES } from '../data/tailwindColors'

interface MotifPickerProps {
  selectedMotifId: MotifId
  onSelect: (motifId: MotifId) => void
}

export function MotifPicker({ selectedMotifId, onSelect }: MotifPickerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const selectedMotif = useMemo(
    () => MOTIFS.find((motif) => motif.id === selectedMotifId),
    [selectedMotifId]
  )

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            Motif
          </h2>
          <button
            onClick={() => setIsOpen((prev) => !prev)}
            className="px-3 py-1.5 text-xs font-semibold text-gray-700 border border-gray-200 rounded-md hover:border-gray-300 hover:text-gray-900 cursor-pointer"
          >
            {isOpen ? 'Collapse' : 'Edit'}
          </button>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span
            className="inline-flex h-2.5 w-2.5 rounded-full"
            style={{
              backgroundColor: selectedMotif
                ? COLOR_VALUES[selectedMotif.tokens.primaryColor]
                : '#111827',
            }}
          />
          <span className="font-medium text-gray-700">
            {selectedMotif?.name ?? 'Custom'}
          </span>
          <span className="text-gray-400">·</span>
          <span className="truncate max-w-[180px]">{selectedMotif?.description}</span>
        </div>
      </div>

      {isOpen && (
        <div className="grid grid-cols-2 gap-3">
          {MOTIFS.map((motif) => {
            const isSelected = motif.id === selectedMotifId
            const primaryColor = COLOR_VALUES[motif.tokens.primaryColor]

            return (
              <button
              key={motif.id}
              onClick={() => onSelect(motif.id)}
              className={`
                relative p-4 rounded-lg border-2 text-left transition-all
                ${
                  isSelected
                    ? 'border-gray-900 bg-gray-50 shadow-sm'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                } cursor-pointer
              `}
            >
                <div className="flex items-start gap-3">
                  <div
                    className="w-8 h-8 rounded-md flex-shrink-0"
                    style={{ backgroundColor: primaryColor }}
                  />
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900 text-sm">{motif.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                      {motif.description}
                    </div>
                  </div>
                </div>
                {isSelected && (
                  <div className="absolute top-2 right-2">
                    <svg
                      className="w-5 h-5 text-gray-900"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
